
import Vue from 'vue';
import _sortBy from 'lodash/sortBy';
import _isEmpty from 'lodash/isEmpty';
import { functions } from '@/firebase';
import { mapActions, mapState } from 'vuex';
import {
	SNACKBAR_STATUS,
	RUNNITS_PUBLISHED_STATE,
	RunnitsPublishedState,
} from '@/constants/constants';
import { FEATURED_RUNNIT_NODE_DEF_IDS, RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import {
	RunnitNodeDef,
	RunnitNodeDefToolType,
	UserRunnitNodeDefInfo,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE_SORT,
	RUNNIT_NODE_DEF_TOOL_TYPE,
	RUNNIT_TYPE,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RunnitNodeDefTag,
	RunnitNodeDefTagType,
} from '@run-diffusion/shared';
import OfferingCardWithLabels from '@/components/OfferingCardWithLabels.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import ComplexBackground from '@/components/designElements/ComplexBackground.vue';
import EmptyStateCircleBackground from '@/components/states/EmptyStateCircleBackground.vue';
import RunnitDuplicateEditNodeDefInternalEditor from '@/views/Runnits/RunnitSettings/internalAdminOnly/RunnitDuplicateEditNodeDefInternalEditor.vue';
import { RunnitsCRUDMixin, TeamUserMixin } from '@/mixins';
import { ROUTER } from '@/router/constants';
import DiagonalStackedSquaresSVG from '@/assets/DiagonalStackedSquaresSVG.vue';
import RunnitTagTypeSelect from './RunnitTagTypeSelect.vue';
import RunnitTagFilter from './RunnitTagFilter.vue';
import { smartFilter } from '@/utils/fuzzySearch';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import RunnitNavTabs from '@/views/Runnits/base/RunnitNavTabs.vue';
import KitToolsFiltersAndSelection from '@/views/Runnits/pages/KitPages/components/KitToolsFiltersAndSelection.vue';
import { KIT_TYPE, KitType } from '@/views/Runnits/constants';
import { getRunnitsToolsDialogNavTab, setKitPreference, setRunnitsToolsDialogNavTab } from '@/utils/localStorage';
import { KitsMixin } from '@/mixins/KitsMixin';


export type ToolsDialogNav = 'KITS' | 'RECENT' | 'TOOLS' | 'APPS';
export const TOOLS_DIALOG_NAV: Record<ToolsDialogNav, ToolsDialogNav> = {
	'KITS': 'KITS',
	'RECENT': 'RECENT',
	'TOOLS': 'TOOLS',
	'APPS': 'APPS',
};

// Read the last-used kit from localStorage and validate it against the
// `KIT_TYPE` enum. A stale or unrecognized value falls back to null so
// the dialog shows the kit picker, same as a brand-new user. Extracted
// because the same restore happens twice — once on initial `data()` to
// hydrate component state, and once in the `currNavTab` watcher when
// the user returns to the Kits tab from Tools/Apps/Recent.
export default Vue.extend({
	name: 'RunnitToolsLibraryDialog',
	props: {
		value: { type: Boolean, default: false },
		persistent: { type: Boolean, default: false },
		maxWidth: { type: [Number, String], default: '1150px' },
		singleToolMode: { type: Boolean, default: false },
		templateMode: { type: Boolean, default: false },
	},
	mixins: [
		RunnitsCRUDMixin,
		TeamUserMixin,
		KitsMixin,
	],
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			TOOLS_DIALOG_NAV,
			RUNNITS_PUBLISHED_STATE,
			RUNNIT_NODE_DEF_TOOL_TYPE,
			FEATURED_RUNNIT_NODE_DEF_IDS,
			KIT_TYPE,
			open: false,
			toolSearchValue: '',

			filteredRunnitNodeDefs: [],

			nodeDefInternalEditorDialog: {
				open: false,
				selectedNodeDef: null,
			},

			publishedStateFilter: RUNNITS_PUBLISHED_STATE.PUBLISHED,
			showRunnitUpsell: false,

			filtersCollapsed: false,
			selectedTagsFilterMap: {},

			nodeDefsByTagMap: {},

			isAllCategorySelected: true,
			isCompanyCategorySelected: false,
			selectedTypes: [],

			currNavTab: TOOLS_DIALOG_NAV.KITS,
			// The tab restored on open. RunnitNavTabs emits its initial tab when
			// it mounts (on first open), so passing a fixed KITS there would
			// overwrite the restored tab.
			initialNavTab: TOOLS_DIALOG_NAV.KITS,
			toolsDialogNavTabs: [
				{
					id: TOOLS_DIALOG_NAV.KITS,
					label: 'Generation Kits',
					icon: 'mdi-toolbox',
				},
				{
					id: TOOLS_DIALOG_NAV.RECENT,
					label: 'Recent Tools',
					icon: 'mdi-history',
				},
				{
					id: TOOLS_DIALOG_NAV.TOOLS,
					label: 'Tools',
					icon: 'mdi-tools',
				},
				{
					id: TOOLS_DIALOG_NAV.APPS,
					label: 'Apps',
					icon: 'mdi-apps',
				},
			],

			// Kit selection state — restored from localStorage above. Matters
			// most in single-tool mode, where picking a tool navigates to a
			// new /runnit/:id and remounts the whole dialog (in-memory state
			// alone wouldn't survive that). On boards the dialog stays
			// mounted across opens, so the localStorage layer is functionally
			// redundant but harmless.
			// Every open starts on the Kit picker cards, even when the saved
			// nav tab is Kits; see the `value` watcher.
			selectedKitType: null,
			showKitTypeSelection: true,
			// The Kit drilled into during this dialog session, so switching to
			// another nav tab and back does not lose the user's place.
			sessionKitType: null,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'limitNumRunnits',
			'loadingLimitNumRunnits',
			'loadingRecentUserRunnitNodeDefInfos',
			'recentUserRunnitNodeDefInfos',
			'runnitState',
			'publicRunnitNodeDefs',
			'loadingPublicRunnitNodeDefs',
			'companyRunnitNodeDefs',
			'loadingCompanyRunnitNodeDefs',
			'publicRunnitNodeDefTagsMap',
		]),
		// Recently-used tools filtered to the current user/team context.
		// Returns the full list; consumers slice as needed (e.g. the inline
		// "Recently Used" group above the Tools tab takes the top 5, while
		// the dedicated Recent Tools tab shows everything).
		allRecentRunnitNodeDefInfos () {
			return this.recentUserRunnitNodeDefInfos.filter((toolInfo: UserRunnitNodeDefInfo) => (
				!toolInfo.nodeDef.isDeleted &&
				(
					this.user &&
					toolInfo.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT &&
					(
						this.user.isAdmin ||
						toolInfo.nodeDef.isPublished
					)
				) &&
				(
					toolInfo.nodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
					(
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team &&
						toolInfo.nodeDef.companyIds?.[this.team.companyId]
					)
				) &&
				!this.isNodeDefBlockedForTeamUser(toolInfo.nodeDef)
			));
		},
		visibleRunnitNodeDefInfos () {
			return this.allRecentRunnitNodeDefInfos.slice(0, 5);
		},
		visibleRunnitNodeDefs () {
			return _sortBy(this.filteredRunnitNodeDefs.filter((nodeDef: RunnitNodeDef) => (
				this.filteredTagTypes.some(tag => _isEmpty(nodeDef.tags) || nodeDef.tags[tag])
			)), 'title');
		},
		tagsOnSelectedType () {
			const excludedTypes: RunnitNodeDefTagType[] = [
				RUNNIT_NODE_DEF_TAG_TYPE.HOME_PAGE,
				RUNNIT_NODE_DEF_TAG_TYPE.INDUSTRY,
				RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_TYPE,
				RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY,
				RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL,
			];
			return this.sortedTagType.map((tagId) => (this.tagsById[tagId])).filter(t => t && (this.user.isAdmin || !excludedTypes.includes(t.type)));
		},
		noFilterTagsSelected () {
			return !Object.keys(this.selectedTagsFilterMap).length ||
				Object.keys(this.selectedTagsFilterMap).every(tagId => !this.selectedTagsFilterMap[tagId]);
		},
		filteredTagTypes () {
			const hiddenTagTypes = [RUNNIT_NODE_DEF_TAG_TYPE.HOME_PAGE];
			return this.sortedTagType.filter(tagId => (
				(this.noFilterTagsSelected && !hiddenTagTypes.includes(this._get(this.tagsById, `[${tagId}].type`))) ||
				this.selectedTagsFilterMap[tagId]
			));
		},
		sortedTagType () {
			return _sortBy(Object.keys(this.nodeDefsByTagMap), [
				(tagId) => { return RUNNIT_NODE_DEF_TAG_TYPE_SORT[this._get(this.tagsById, `[${tagId}].type`)] },
				(tagId) => { return this._get(this.tagsById, `[${tagId}].sortOrder`) },
				(tagId) => { return this._get(this.tagsById, `[${tagId}].label`) },
			])
				.filter(tagId => (
					this.isAllCategorySelected ||
					this.isCompanyCategorySelected ||
					this.selectedTypes.some(type => (
						this._get(this.tagsById, `[${tagId}].type`) === type ||
						tagId === 'uncategorized'
					)))
				);
		},
		tagsById (): Record<string, RunnitNodeDefTag> {
			return {
				'uncategorized': { label: 'Uncategorized', id: 'uncategorized', type: 'uncategorized' },
				...this.publicRunnitNodeDefTagsMap,
			} as Record<string, RunnitNodeDefTag>;
		},
		tabTagsMap (): Record<ToolsDialogNav, string> {
			const internalTags: RunnitNodeDefTag[] = Object.values(this.tagsById).filter((tag: RunnitNodeDefTag) => tag.type === RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL) as RunnitNodeDefTag[];
			const appTag: RunnitNodeDefTag = internalTags.find((tag: RunnitNodeDefTag) => tag.label === 'App') || null;
			const toolTag: RunnitNodeDefTag = internalTags.find((tag: RunnitNodeDefTag) => tag.label === 'Tool') || null;

			return {
				[TOOLS_DIALOG_NAV.TOOLS]: toolTag?.id || null,
				[TOOLS_DIALOG_NAV.APPS]: appTag?.id || null,
			} as Record<ToolsDialogNav, string>;
		},
		kitOptions () {
			// One resolver, shared with RunnitSingleTool: tools whose nodeDef is
			// missing, unpublished, deleted, or blocked for this team user are
			// already gone, and the curated order is intact.
			return this.getResolvedKit(this.selectedKitType)?.tools || [];
		},
		kitTypeCards () {
			// Entirely data: which Kits, their order, name, icon, and blurb all
			// come from the runnitKits documents, so curating a Kit card needs
			// no deploy.
			return (this.navigableKits || []).map((kit) => ({
				type: kit.id,
				title: kit.label,
				icon: kit.icon,
				description: kit.description,
			}));
		},
		selectedKitTypeTitle () {
			if (!this.selectedKitType) return '';
			const kitCard = this.kitTypeCards.find(card => card.type === this.selectedKitType);
			return kitCard ? kitCard.title : '';
		},
		selectedKitTypeIcon () {
			if (!this.selectedKitType) return '';
			const kitCard = this.kitTypeCards.find(card => card.type === this.selectedKitType);
			return kitCard ? kitCard.icon : '';
		},
	},
	watch: {
		value: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				this.open = !!newVal;

				// Every open starts on the nav tab the user last picked (Kits
				// when nothing valid is saved), backed out to the Kit cards.
				// data() runs once and the dialog stays mounted, so without
				// this a re-open keeps whichever Kit was showing last time.
				if (newVal && !oldVal) {
					this.initialNavTab = TOOLS_DIALOG_NAV[getRunnitsToolsDialogNavTab() as ToolsDialogNav] || TOOLS_DIALOG_NAV.KITS;
					this.currNavTab = this.initialNavTab;
					this.selectedKitType = null;
					this.showKitTypeSelection = true;
					this.sessionKitType = null;
					// The Apps and Tools lists are filtered by nav tab, so refilter
					// for the restored tab rather than showing the last one's list.
					this.applyFilters();
				}
			},
		},
		filteredRunnitNodeDefs: {
			immediate: true,
			handler (newVal: RunnitNodeDef[]) {
				this.mapNodeDefsToTag(newVal);
			}
		},
		open: {
			immediate: true,
			handler (newVal: boolean) {
				if (
					newVal && // the dialog is open
					!this.showRunnitUpsell && // the upsell is not already open
					this.singleToolMode && // not adding a tool to an existing runnit
					!this.user?.clubInfo?.limit?.unlimitedRunnits && // the user does not get unlimited runnits
					!this.limitNumRunnits.length && // the user does not already have a runnit
					this.loadingLimitNumRunnits === false && // done loading limitNumRunnits
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER && // on personal runnits view, not team
					this.loadingRecentUserRunnitNodeDefInfos === false && // done loading recents
					this.recentUserRunnitNodeDefInfos.length >= 3 // the user has opened at least 3 tools
				) {
					setTimeout(() => {
						this.showRunnitUpsell = true;
					}, 1000);
				}
			}
		},
		loadingPublicRunnitNodeDefs: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal && !newVal && !this.loadingCompanyRunnitNodeDefs) {
					this.applyFilters();
				}
			}
		},
		loadingCompanyRunnitNodeDefs: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal && !newVal && !this.loadingPublicRunnitNodeDefs) {
					this.applyFilters();
				}
			}
		},
		currNavTab: {
			handler (newVal: ToolsDialogNav) {
				setRunnitsToolsDialogNavTab(newVal);
				if (newVal !== TOOLS_DIALOG_NAV.KITS) {
					// Leaving Kits: remember the drill-down, then drop it, so a
					// later return doesn't flash the old tool list mid-transition.
					this.sessionKitType = this.selectedKitType;
					this.showKitTypeSelection = true;
					this.selectedKitType = null;
				} else if (this.sessionKitType) {
					// Returning to Kits: restore only the Kit the user opened
					// during THIS dialog session, so a tab toggle does not lose
					// their place. Deliberately not restored from localStorage
					// or the route: the dialog opens on the picker now, and
					// bouncing into a Kit they never chose is the surprise that
					// change was meant to remove.
					this.selectedKitType = this.sessionKitType;
					this.showKitTypeSelection = false;
				}
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		setOpen (val: boolean) {
			this.open = !!val;
			if (this.open !== this.value) {
				this.$emit('input', this.open);
			}
		},
		onCancel () {
			this.setOpen(false);
			this.toolSearchValue = '';
		},
		onSelectionChosen () {
			this.onCancel();
		},
		onSetSelectedTypes (selectedTypes: RunnitNodeDefToolType[]) {
			this.selectedTypes = selectedTypes;
			this.restTagFilterMap();
			this.applyFilters();
			if (this.$vuetify.breakpoint.smAndDown) {
				this.toggleFilterCollapse();
			}
		},
		handleMultiSelectFilter (selectedTypes: RunnitNodeDefToolType[]) {
			this.isAllCategorySelected = false;
			this.isCompanyCategorySelected = false;
			this.onSetSelectedTypes(selectedTypes);
		},
		handleAllSelectFilter () {
			this.isAllCategorySelected = true;
			this.isCompanyCategorySelected = false;
			this.onSetSelectedTypes([]);
		},
		handleCompanySelectFilter () {
			this.isAllCategorySelected = false;
			this.isCompanyCategorySelected = true;
			this.onSetSelectedTypes([]);
		},
		handleSearchInput (searchValue) {
			this.toolSearchValue = searchValue;
			this.applyFilters();
		},
		handlePublishedFilterChange (publishedState: RunnitsPublishedState) {
			this.publishedStateFilter = publishedState;
			this.applyFilters();
		},
		applyFilters () {
			this.filteredRunnitNodeDefs = [];
			[
				...this.publicRunnitNodeDefs,
				...this.companyRunnitNodeDefs,
			].forEach((runnitNodeDef: RunnitNodeDef) => {
				let matchesSearch: boolean, matchesPublishedState: boolean, matchesSelectedTypes: boolean, matchesCompany: boolean, matchesNavTab: boolean, matchesNotBlocked: boolean;

				if (this.currNavTab === TOOLS_DIALOG_NAV.APPS) {
					const appTagId = this.tabTagsMap[TOOLS_DIALOG_NAV.APPS];
					// Apps tab: Only show tools explicitly tagged as Apps (strict filtering)
					matchesNavTab = appTagId ? !!runnitNodeDef.tags?.[appTagId] : false;
				} else if (this.currNavTab === TOOLS_DIALOG_NAV.TOOLS) {
					const appTagId = this.tabTagsMap[TOOLS_DIALOG_NAV.APPS];
					// Tools tab: Show everything that's NOT explicitly tagged as an App (inclusive filtering)
					matchesNavTab = appTagId ? !runnitNodeDef.tags?.[appTagId] : true;
				} else {
					matchesNavTab = true;
				}

				if (!this.toolSearchValue) {
					matchesSearch = true;
				} else {
					matchesSearch = smartFilter(
						[runnitNodeDef],
						this.toolSearchValue,
						[
							{ name: 'title', weight: 0.7 },
							{ name: 'description', weight: 0.3 }
						]
					).length > 0;
				}

				matchesPublishedState = !!(
					(this.publishedStateFilter === RUNNITS_PUBLISHED_STATE.PUBLISHED && runnitNodeDef.publishedAt) ||
					(this.publishedStateFilter === RUNNITS_PUBLISHED_STATE.NOT_PUBLISHED && !runnitNodeDef.publishedAt)
				);

				if (this.isCompanyCategorySelected) {
					matchesSelectedTypes = runnitNodeDef.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY;
				} else if (this.selectedTypes && this.selectedTypes.length && !this.isAllCategorySelected) {
					matchesSelectedTypes = this.selectedTypes.find(type => (
						_isEmpty(runnitNodeDef.tagTypes) ||
						runnitNodeDef.tagTypes[type] ||
						Object.values(runnitNodeDef.tagTypes).every(applied => !applied)
					));
				} else {
					matchesSelectedTypes = true;
				}

				matchesCompany = !!(
					runnitNodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
					(
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team?.companyId &&
						runnitNodeDef.companyIds?.[this.team.companyId]
					)
				);

				matchesNotBlocked = !this.isNodeDefBlockedForTeamUser(runnitNodeDef);

				if (matchesSearch && matchesPublishedState && matchesSelectedTypes && matchesCompany && matchesNavTab && matchesNotBlocked) {
					this.filteredRunnitNodeDefs.push(runnitNodeDef);
				}
			});

			this.filteredRunnitNodeDefs = _sortBy(this.filteredRunnitNodeDefs, 'sortOrder');
		},
		async onToolCardClick (runnitNodeDef: RunnitNodeDef) {
			if (this.templateMode) {
				this.$emit('on-template-tool-selected', runnitNodeDef);
				this.onCancel();
				return;
			}
			if (this.singleToolMode) {
				await this.onAddRunnitClick(runnitNodeDef, RUNNIT_TYPE.SINGLE_TOOL);
				this.onCancel();
				return;
			}
			const onError: Function = (e) => {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error adding a tool to this Board, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			};
			try {
				this.onCancel();
				this.$emit('on-adding-tool-loading', true);
				const functionRef = functions
					.httpsCallable('addNodeToRunnit');
				const { success } = (await functionRef({
					runnitId: this.$route.params.runnitId,
					nodeDefId: runnitNodeDef.id,
				})).data;
				if (!success) {
					onError(new Error('addNodeToRunnit returned success: false'));
				}
			} catch (e) {
				onError(e);
			} finally {
				this.$emit('on-adding-tool-loading', false);
			}
		},
		toggleAdminNodeDefEditor (selectedNodeDef: RunnitNodeDef) {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: true,
				selectedNodeDef,
			};
		},
		onEditDialogClose (open: boolean) {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: !!open,
			};
			this.applyFilters();
		},
		routeToPrivateRunnits () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNIT_BOARDS_PAGE });
			this.setOpen(false);
		},
		restTagFilterMap () {
			this.selectedTagsFilterMap = {};
		},
		toggleTagFilter (tag) {
			this.selectedTagsFilterMap = {
				...this.selectedTagsFilterMap,
				[tag.id]: !this.selectedTagsFilterMap[tag.id],
			};
		},
		async mapNodeDefsToTag (nodeDefs) {
			this.nodeDefsByTagMap = (nodeDefs || []).reduce((map, nodeDef) => {
				Object.keys(nodeDef.tags || []).map((tagId) => {
					if (!map[tagId]) {
						map[tagId] = [];
					}
					map[tagId].push(nodeDef);
				})
				if (!nodeDef.tags || _isEmpty(nodeDef.tags)) {
					if (!map['uncategorized']) {
						map['uncategorized'] = [];
					}
					map['uncategorized'].push(nodeDef);
				}
				return map;
			}, {});
		},
		toggleFilterCollapse () {
			this.filtersCollapsed = !this.filtersCollapsed;
		},
		handleNavTabChange (navTab: ToolsDialogNav) {
			this.currNavTab = navTab;
			this.applyFilters();
		},
		onKitOptionSelect (option) {
			// Find the corresponding RunnitNodeDef from all available node defs
			const runnitNodeDef = [
				...this.publicRunnitNodeDefs,
				...this.companyRunnitNodeDefs,
			].find(nodeDef => nodeDef.id === option.value);

			if (runnitNodeDef) {
				// Remember this as the Kit's last-used tool, so re-entering the
				// Kit from the side nav returns here instead of the default.
				// KitDialogSelect already did this for the single-tool picker;
				// without it here, picks made in this dialog were never
				// recorded and every entry fell back to the default.
				if (this.selectedKitType) {
					setKitPreference(this.selectedKitType, String(option.value));
				}
				this.onToolCardClick(runnitNodeDef);
			}
		},
		onGoToAllTools () {
			// Switch to the Tools tab to show all available tools
			this.currNavTab = TOOLS_DIALOG_NAV.TOOLS;
		},
		selectKitType (kitType: KitType) {
			this.selectedKitType = kitType;
			this.showKitTypeSelection = false;
			this.sessionKitType = kitType;
		},
		goBackToKitTypeSelection () {
			this.showKitTypeSelection = true;
			this.selectedKitType = null;
			this.sessionKitType = null;
		},
	},
	beforeDestroy () {
		if (this.open) {
			this.setOpen(false);
		}
	},
	components: {
		GlassButton,
		LoadingState,
		OfferingCardWithLabels,
		ComplexBackground,
		EmptyStateCircleBackground,
		RunnitDuplicateEditNodeDefInternalEditor,
		DiagonalStackedSquaresSVG,
		RunnitTagTypeSelect,
		RunnitTagFilter,
		DialogOrBottomSheet,
		RunnitNavTabs,
		KitToolsFiltersAndSelection,
	},
});
